import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Box,
  Typography, Card, CardContent, Chip, Divider, Table, TableBody,
  TableRow, TableCell, Stack
} from '@mui/material';
import { Visibility, Close } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { calculateUnitCost } from '../utils/ingredientUtils.js';

const IngredientDetailsDialog = ({ open, onClose, ingredient }) => {
  const { t } = useTranslation();

  if (!ingredient) return null;

  const unitCost = calculateUnitCost(ingredient.package_cost, ingredient.package_size);
  const stockValue = parseFloat(ingredient.current_stock || 0) * unitCost;
  const unitLabel = ingredient.package_uom || ingredient.unit_of_measure || 'pcs';

  const getStockStatus = () => {
    const current = parseFloat(ingredient.current_stock || 0);
    const min = parseFloat(ingredient.min_stock || 0);
    
    if (current === 0) return { text: 'Out of Stock', color: 'error' };
    if (current <= min) return { text: 'Low Stock', color: 'warning' };
    return { text: 'Normal', color: 'success' };
  };

  const stockStatus = getStockStatus();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Visibility />
        {t('ingredients.details')} - {ingredient.name}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6} sx={{ mt: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ingredients.basicInformation')}
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                        {t('ingredients.name')}:
                      </TableCell>
                      <TableCell>{ingredient.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('ingredients.type')}:
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={ingredient.type === 'premade' ? t('ingredients.premade') : t('ingredients.raw')}
                          color={ingredient.type === 'premade' ? 'secondary' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    {ingredient.description && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t('ingredients.description')}:
                        </TableCell>
                        <TableCell>{ingredient.description}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('ingredients.status')}:
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={ingredient.is_active ? t('ingredients.active') : t('ingredients.inactive')}
                          color={ingredient.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Package & Cost Information */}
          <Grid item xs={12} md={6} sx={{ mt: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ingredients.packageInformation')}
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                        {t('ingredients.packageSize')}:
                      </TableCell>
                      <TableCell>{ingredient.package_size} {unitLabel}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('ingredients.packageCost')}:
                      </TableCell>
                      <TableCell>${parseFloat(ingredient.package_cost || 0).toFixed(2)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {t('ingredients.unitCost')}:
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          ${unitCost.toFixed(4)} per {unitLabel}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Stock Information */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('ingredients.stockInformation')}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('ingredients.currentStock')}
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700,
                        color: stockStatus.color === 'error' ? 'error.main' : 
                               stockStatus.color === 'warning' ? 'warning.main' : 'success.main'
                      }}>
                        {parseFloat(ingredient.current_stock || 0)} {unitLabel}
                      </Typography>
                      <Chip label={stockStatus.text} color={stockStatus.color} size="small" sx={{ mt: 1 }} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('ingredients.stockValue')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                        ${stockValue.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('ingredients.minStock')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {parseFloat(ingredient.min_stock || 0)} {unitLabel}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('ingredients.maxStock')}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {parseFloat(ingredient.max_stock || 0)} {unitLabel}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional Information */}
          {(ingredient.supplier_name || ingredient.storage_location || ingredient.barcode) && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('ingredients.additionalInformation')}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {ingredient.supplier_name && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: '30%' }}>
                            {t('ingredients.supplier')}:
                          </TableCell>
                          <TableCell>{ingredient.supplier_name}</TableCell>
                        </TableRow>
                      )}
                      {ingredient.storage_location && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('ingredients.storageLocation')}:
                          </TableCell>
                          <TableCell>{ingredient.storage_location}</TableCell>
                        </TableRow>
                      )}
                      {ingredient.barcode && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('ingredients.barcode')}:
                          </TableCell>
                          <TableCell>{ingredient.barcode}</TableCell>
                        </TableRow>
                      )}
                      {ingredient.shelf_life_after_opening && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('ingredients.shelfLifeAfterOpening')}:
                          </TableCell>
                          <TableCell>{ingredient.shelf_life_after_opening} days</TableCell>
                        </TableRow>
                      )}
                      {ingredient.purchase_date && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('ingredients.purchaseDate')}:
                          </TableCell>
                          <TableCell>{new Date(ingredient.purchase_date).toLocaleDateString()}</TableCell>
                        </TableRow>
                      )}
                      {ingredient.expiry_date_on_purchase && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('ingredients.expiryDate')}:
                          </TableCell>
                          <TableCell>
                            {new Date(ingredient.expiry_date_on_purchase).toLocaleDateString()}
                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                              ({formatDistanceToNow(new Date(ingredient.expiry_date_on_purchase), { addSuffix: true })})
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} startIcon={<Close />}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IngredientDetailsDialog;